import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import locale from "../../../constants/locale";
import {
  BoardColummType,
  BoardItemCard,
  Chat,
  CustomField,
  UserType,
} from "../../../models";
import { useStore } from "../../../stores";
import styles from "./SendToBoard.module.scss";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { v4 as uuid } from "uuid";
import { AntSelect, DatePicker, Field, Loader } from "../../../components";
import { onlyDecimalNumbers } from "../../../utils/numbers";
import { toast } from "bulma-toast";
import { ManageFieldModal } from "../../board/components/CustomFields";
import { DateTime } from "luxon";

type Props = {
  contactId: string;
  contact?: Chat;
  className?: string;
  displayCardButton?: boolean;
  setHasActiveDeal?: (hasActiveDeal: boolean) => void;
};

const SendToBoard = ({ setHasActiveDeal, contact, className }: Props) => {
  const { t } = useTranslation();
  const { user, getPermissions } = useStore("auth");
  const { selectedBot } = useStore("bots");
  const { users } = useStore("users");
  const [findingDeal, setFindingDeal] = useState(true);
  const [activeDeal, setActiveDeal] = useState<BoardItemCard>();
  const [stageId, setStageId] = useState<number>();
  const {
    boards,
    selectedBoard,
    updateItemToBoard,
    deleteItemFromBoard,
    loadBoards,
    selectBoard,
    addItemToBoard,
    findDealByContact,
  } = useStore("boards");

  const [deletingItem, setDeletingItem] = useState(false);

  useEffect(() => {
    if (contact?.id) {
      const load = async () => {
        try {
          setFindingDeal(true);
          const deal = await findDealByContact(contact?.id);
          setActiveDeal(deal);
          setStageId(deal?.columnId);
        } catch (e) {
          console.log(e);
        } finally {
          setFindingDeal(false);
        }
      };
      load();
    }
  }, [contact?.id, findDealByContact]);

  const deleteItem = async () => {
    if (!activeDeal?.id || !activeDeal?.columnId) return;
    setDeletingItem(true);

    try {
      await deleteItemFromBoard(activeDeal, activeDeal.columnId);
      setDeletingItem(false);
      setActiveDeal(undefined);
    } catch (error) {
      setDeletingItem(false);
      if (error instanceof Error) {
        toast({
          message: t(locale.genericError),
          position: "top-right",
          dismissible: true,
          type: "is-danger",
          pauseOnHover: true,
        });
      }
    }
  };

  const isCollaborator = activeDeal && user?.type === UserType.Collaborator;
  const hasClosedLost = useMemo(
    () =>
      selectedBoard?.columns?.find((f) => f.id === stageId)?.type ===
      BoardColummType.ClosedLost,
    [selectedBoard, stageId]
  );

  useEffect(() => {
    if (setHasActiveDeal) {
      setHasActiveDeal(activeDeal?.id ? true : false);
    }
  }, [activeDeal, setHasActiveDeal]);

  const collaboratorCanDelete = useMemo(
    () => getPermissions()?.canCreateOrDeleteDeals,
    [getPermissions]
  );

  useEffect(() => {
    if (!selectedBot?.id) {
      return;
    }

    loadBoards();
  }, [loadBoards, selectedBot?.id]);

  useEffect(() => {
    if ((activeDeal as any)?.boardId) {
      const board = boards?.find((f) => f.id === (activeDeal as any).boardId);
      if (board) {
        selectBoard(board?.uuid);
      }
    }
  }, [activeDeal, boards, selectBoard]);

  return (
    <div
      className={
        className || "my-2 has-background-light has-rounded-corners p-2"
      }
    >
      <Formik
        key={activeDeal?.id}
        initialValues={{
          boardId: undefined,
          subscriberId: contact?.id,
          name: "",
          assigneeId: undefined,
          opportunity: "",
          columnId: undefined,
          ...activeDeal,
          dueDate: activeDeal?.dueDate
            ? activeDeal.dueDate.toJSDate()
            : undefined,
          closedLostReason: activeDeal?.closedLostReason ?? "",
          customFields: activeDeal?.customFields?.map((f: any) => ({
            ...f,
            value:
              f.type === "date" && (f.value as any)?.toDate
                ? (f.value as any)?.toDate()
                : f.value,
          })),
        }}
        validationSchema={yup.object({
          boardId: yup.string().required(t(locale.fieldRequired)),
          name: yup.string().required(t(locale.fieldRequired)),
          columnId: yup.string().required(t(locale.fieldRequired)),
          dueDate: yup.date().required(t(locale.fieldRequired)),
          assigneeId: yup.string().required(t(locale.fieldRequired)),
          opportunity: yup.number().required(t(locale.fieldRequired)),
          ...(hasClosedLost && {
            closedLostReason: yup
              .string()
              .max(30)
              .required(t(locale.fieldRequired)),
          }),
        })}
        onSubmit={async (data) => {
          try {
            // Convert JavaScript Date to Luxon DateTime
            const dueDateLuxon = data.dueDate
              ? DateTime.fromJSDate(data.dueDate as any)
              : undefined;

            if (activeDeal?.id) {
              const deal = await updateItemToBoard(
                {
                  ...activeDeal,
                  ...data,
                  dueDate: dueDateLuxon,
                  opportunity: parseFloat(
                    data.opportunity as unknown as string
                  ),
                  updatedAt: new Date(),
                } as any,
                data.columnId as never,
                activeDeal.columnId as never,
                activeDeal?.assigneeId
              );
              setActiveDeal(deal);
            } else {
              const deal = await addItemToBoard(
                {
                  id: uuid(),
                  type: "card",
                  ...data,
                  dueDate: dueDateLuxon,
                  opportunity: parseFloat(
                    data.opportunity as unknown as string
                  ),
                } as any,
                data.columnId as never
              );
              setActiveDeal(deal);
            }
          } catch (error: any) {
            console.log("error", error);
            let message = t(locale.genericError);
            if (error?.status === 400) {
              message = error.response.data.error;
            }
            toast({
              message,
              position: "top-right",
              dismissible: true,
              type: "is-danger",
              pauseOnHover: true,
            });
          }
        }}
      >
        {({ isSubmitting, submitForm, values, setFieldValue }) => (
          <Form>
            <AntSelect
              size="middle"
              name="boardId"
              showSearch
              filterOption={(input: any, option: any) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              label={t(locale.sendToPipeline)}
              allowClear
              disabled={isCollaborator || Boolean(activeDeal?.id)}
              onChange={(boardId: any) => {
                const board = boards?.find((f) => f.id === boardId);
                if (board) {
                  selectBoard(board?.uuid);
                }
              }}
              options={
                boards?.map((f) => ({
                  value: f.id,
                  label: f.name,
                })) || []
              }
            />
            <AntSelect
              size="middle"
              showSearch
              filterOption={(input: any, option: any) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              name="columnId"
              label={t(locale.stage)}
              disabled={isSubmitting}
              options={
                selectedBoard?.columns?.map((f) => ({
                  value: f.id,
                  label: f.name,
                })) || []
              }
              onChange={(columnId: any) => {
                setStageId(columnId);
              }}
            />
            {hasClosedLost && (
              <Field
                name="closedLostReason"
                label={t(locale.reason)}
                placeholder={"Enter " + t(locale.reason)}
                disabled={isSubmitting}
                inputProps={{
                  maxLength: 30,
                }}
                showCounter
              />
            )}
            <Field
              name="name"
              label={t(locale.name)}
              placeholder={"Enter " + t(locale.name)}
              disabled={isCollaborator || isSubmitting}
            />
            <DatePicker
              name="dueDate"
              label={t(locale.estimatedCloseDate)}
              disabled={isSubmitting}
              minDate={new Date()}
              wrapperClassName="w-100"
              showTimeInput
              dateFormat="MM/dd/yyyy h:mm aa"
            />
            <AntSelect
              size="middle"
              showSearch
              filterOption={(input: any, option: any) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              name="assigneeId"
              label={t(locale.assignee)}
              allowClear
              disabled={isCollaborator || isSubmitting}
              options={
                users?.map((f) => ({
                  value: f.id,
                  label: f.name || f.email,
                })) || []
              }
            />
            <Field
              name="opportunity"
              label={t(locale.opportunity)}
              placeholder={"Enter " + t(locale.opportunity)}
              disabled={isSubmitting}
              inputProps={{
                onKeyDown: onlyDecimalNumbers,
              }}
            />
            <DealCustomFields
              value={values.customFields ?? []}
              onChange={(v) => setFieldValue("customFields", v)}
            />
            <div
              className="is-flex is-justify-content-space-between mt-4"
              style={{ gap: "5px" }}
            >
              <button
                className={clsx(styles.actionBtn, "button mt-2 is-primary", {
                  [styles.addBtn]: true,
                })}
                type="button"
                onClick={submitForm}
                disabled={isSubmitting}
              >
                <span className="icon">
                  <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M4.65833 12.0589C4.65833 12.1428 4.70491 12.2197 4.77898 12.2589C4.853 12.2981 4.94299 12.293 5.01217 12.2457L6.57026 11.1818L4.65835 10.2705V12.0589H4.65833Z" />
                    <path d="M13.9273 0.0601007C13.8845 0.0206464 13.8292 0 13.7734 0C13.7397 0 13.7062 0.00746663 13.6744 0.0230787L0.223554 6.62109C0.0860717 6.68866 -0.000925893 6.82906 7.43561e-06 6.98263C0.000940765 7.13601 0.089409 7.27544 0.227881 7.34151L3.77289 9.03067L10.8611 3.20471L4.65121 9.44914L9.06741 11.5534C9.12166 11.5794 9.18088 11.5923 9.23948 11.5923C9.28949 11.5923 9.33901 11.5832 9.38616 11.5647C9.48902 11.524 9.57076 11.4425 9.61157 11.3396L13.984 0.309384C14.0186 0.222585 13.996 0.123341 13.9273 0.0601007Z" />
                  </svg>
                </span>
                <span>
                  {t(Boolean(activeDeal?.id) ? locale.update : locale.send)}
                </span>
              </button>
              {!!activeDeal && (
                <button
                  onClick={deleteItem}
                  disabled={
                    deletingItem ||
                    isSubmitting ||
                    (isCollaborator && !collaboratorCanDelete)
                  }
                  className="button p-0 is-no-box-shadow is-ghost has-text-danger"
                >
                  {t(locale.delete)}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
      {findingDeal && (
        <div className="loaderClass">
          <Loader />
        </div>
      )}
    </div>
  );
};

export const DealCustomFields = ({
  value,
  onChange,
}: {
  value: CustomField[];
  onChange: (v?: CustomField[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="button is-ghost is-no-box-shadow p-0"
      >
        {t(locale.manageCustomFields)}
      </button>
      {open && (
        <ManageFieldModal
          customFields={value}
          onUpdate={onChange}
          onClose={() => setOpen(false)}
          inlineValue
        />
      )}
    </>
  );
};

export default observer(SendToBoard);
